import { RefObject, useCallback, useEffect, useState } from "react";

/** Below this, a selection is a stray click-drag rather than a fact worth pinning. */
const MIN_SELECTION_LENGTH = 12;
const MAX_SELECTION_LENGTH = 2000;

export interface ResearchSelection {
	text: string;
	wordCount: number;
	/** Viewport coordinates of the selection, for anchoring the CTA. */
	top: number;
	right: number;
}

/**
 * Tracks a text selection inside the research body so the panel can offer
 * "pin this passage" (PREC-3680). Resolving the range through `composedPath`
 * is unnecessary here — the selection API already reports nodes, which we
 * bounds-check against the body container.
 */
export const useResearchSelection = (
	bodyRef: RefObject<HTMLElement | null>
): { selection: ResearchSelection | null; clearSelection: () => void } => {
	const [selection, setSelection] = useState<ResearchSelection | null>(null);

	const clearSelection = useCallback(() => setSelection(null), []);

	useEffect(() => {
		const handleSelectionEnd = () => {
			const active = window.getSelection();
			const body = bodyRef.current;

			if (!active || active.isCollapsed || !body) {
				setSelection(null);

				return;
			}

			const text = active.toString().trim();

			if (text.length < MIN_SELECTION_LENGTH || text.length > MAX_SELECTION_LENGTH) {
				setSelection(null);

				return;
			}

			// Both ends must sit inside the research body — a selection spanning out of
			// it (or belonging to another panel) is not a research fact.
			if (!body.contains(active.anchorNode) || !body.contains(active.focusNode)) {
				setSelection(null);

				return;
			}

			const rect = active.getRangeAt(0).getBoundingClientRect();
			const bodyRect = body.getBoundingClientRect();

			setSelection({
				text,
				wordCount: text.split(/\s+/).filter(Boolean).length,
				top: rect.top + rect.height / 2,
				right: bodyRect.right,
			});
		};

		document.addEventListener("mouseup", handleSelectionEnd);
		document.addEventListener("keyup", handleSelectionEnd);

		return () => {
			document.removeEventListener("mouseup", handleSelectionEnd);
			document.removeEventListener("keyup", handleSelectionEnd);
		};
	}, [bodyRef]);

	// A scroll moves the anchor out from under the CTA, so drop it rather than
	// leaving a button floating over unrelated text.
	useEffect(() => {
		if (!selection) {
			return;
		}

		const handleScroll = () => setSelection(null);

		document.addEventListener("scroll", handleScroll, true);

		return () => document.removeEventListener("scroll", handleScroll, true);
	}, [selection]);

	return { selection, clearSelection };
};
